import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, Link2, CheckCircle2, AlertTriangle, ExternalLink, Copy, Loader2, Unplug, Save,
} from "lucide-react";

export default function QuickBooksPanel() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ client_id: "", client_secret: "", environment: "sandbox" });
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await apiClient.get("/quickbooks/status");
      setStatus(res.data);
      setForm((f) => ({ ...f, environment: res.data.environment || "sandbox" }));
    } catch {
      toast.error("Failed to fetch QuickBooks status");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleSaveCredentials = async () => {
    setSaving(true);
    try {
      const payload = { environment: form.environment };
      if (form.client_id) payload.client_id = form.client_id;
      if (form.client_secret) payload.client_secret = form.client_secret;
      await apiClient.put("/quickbooks/settings", payload);
      toast.success("QuickBooks credentials saved securely (encrypted)");
      setForm({ ...form, client_id: "", client_secret: "" });
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save credentials");
    } finally {
      setSaving(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await apiClient.get("/quickbooks/connect");
      // Open Intuit consent flow in a new tab so admin can complete OAuth
      window.open(res.data.authorize_url, "_blank", "noopener");
      toast.info("Opening Intuit consent page in a new tab. After approval you'll return to a success page.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start OAuth");
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect QuickBooks? You'll need to re-authorize to sync again.")) return;
    setDisconnecting(true);
    try {
      await apiClient.post("/quickbooks/disconnect");
      toast.success("Disconnected from QuickBooks");
      fetchStatus();
    } catch {
      toast.error("Failed to disconnect");
    } finally {
      setDisconnecting(false);
    }
  };

  const copyRedirect = () => {
    navigator.clipboard.writeText(status?.redirect_uri || "");
    toast.success("Redirect URI copied");
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="quickbooks-panel">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin")} data-testid="qbo-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading text-2xl font-bold text-slate-900">QuickBooks Online</h1>
            <p className="text-slate-500 mt-1">Two-way sync of customers, invoices, and payments</p>
          </div>
          {status?.connected && (
            <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200">
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Connected
            </Badge>
          )}
        </div>

        {/* Connection Status */}
        <Card className="sag-card">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Link2 className="h-5 w-5" /> Connection Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="animate-pulse text-slate-500">Loading…</div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <span className="text-slate-500">Credentials:</span>
                    {status?.configured ? (
                      <span className="text-emerald-700 font-medium">Saved (…{status.client_id_last4})</span>
                    ) : (
                      <span className="text-amber-700 font-medium">Not set</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <span className="text-slate-500">OAuth:</span>
                    {status?.connected ? (
                      <span className="text-emerald-700 font-medium">Connected · Realm {status.realm_id}</span>
                    ) : (
                      <span className="text-slate-500 font-medium">Not connected</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <span className="text-slate-500">Env:</span>
                    <Badge variant="outline" className="uppercase text-[10px]">{status?.environment || "sandbox"}</Badge>
                  </div>
                </div>

                {status?.connected_at && (
                  <p className="text-xs text-slate-500">Connected at {new Date(status.connected_at).toLocaleString()} · Token expires {status.token_expires_at ? new Date(status.token_expires_at).toLocaleString() : "—"}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Step 1: Credentials */}
        <Card className="sag-card">
          <CardHeader>
            <CardTitle className="text-lg font-heading">Step 1 — Enter Intuit Developer credentials</CardTitle>
            <CardDescription>
              Get these from your app at{" "}
              <a href="https://developer.intuit.com/app/developer/myapps" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline inline-flex items-center gap-1">
                developer.intuit.com/myapps <ExternalLink className="h-3 w-3" />
              </a>{" "}
              → your app → <strong>Keys &amp; OAuth</strong>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Environment</Label>
                <Select value={form.environment} onValueChange={(v) => setForm({ ...form, environment: v })}>
                  <SelectTrigger data-testid="qbo-env-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sandbox">Sandbox (safe testing)</SelectItem>
                    <SelectItem value="production">Production (live QBO company)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Redirect URI (whitelist this on Intuit)</Label>
                <div className="flex gap-1 mt-1">
                  <Input value={status?.redirect_uri || ""} readOnly className="font-mono text-xs" data-testid="qbo-redirect-input" />
                  <Button type="button" variant="outline" size="icon" onClick={copyRedirect} data-testid="qbo-copy-redirect">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            <div>
              <Label>Client ID</Label>
              <Input
                type="text"
                placeholder={status?.configured ? `Leave blank to keep existing (…${status.client_id_last4})` : "AB...long string from Intuit"}
                value={form.client_id}
                onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                data-testid="qbo-client-id-input"
              />
            </div>
            <div>
              <Label>Client Secret</Label>
              <Input
                type="password"
                placeholder={status?.configured ? "Leave blank to keep existing" : "Long secret from Intuit"}
                value={form.client_secret}
                onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
                data-testid="qbo-client-secret-input"
              />
              <p className="text-xs text-slate-500 mt-1">Stored encrypted in your database. Never shown again after save.</p>
            </div>

            <Button
              className="w-full bg-sag-navy hover:bg-sag-navy-800"
              onClick={handleSaveCredentials}
              disabled={saving || (!form.client_id && !form.client_secret && form.environment === status?.environment)}
              data-testid="qbo-save-creds-btn"
            >
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : <><Save className="h-4 w-4 mr-2" /> Save Credentials</>}
            </Button>
          </CardContent>
        </Card>

        {/* Step 2: Connect */}
        <Card className="sag-card">
          <CardHeader>
            <CardTitle className="text-lg font-heading">Step 2 — Connect to your QuickBooks company</CardTitle>
            <CardDescription>
              Sign in to Intuit and grant access to the QBO company you want to sync with SAG.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {!status?.configured && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                Save credentials in Step 1 before connecting.
              </div>
            )}
            {status?.connected ? (
              <div className="flex gap-2">
                <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" disabled data-testid="qbo-connected-badge">
                  <CheckCircle2 className="h-4 w-4 mr-2" /> Connected to Realm {status.realm_id}
                </Button>
                <Button variant="outline" onClick={handleDisconnect} disabled={disconnecting} data-testid="qbo-disconnect-btn">
                  {disconnecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Unplug className="h-4 w-4 mr-2" /> Disconnect</>}
                </Button>
              </div>
            ) : (
              <Button
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={handleConnect}
                disabled={connecting || !status?.configured}
                data-testid="qbo-connect-btn"
              >
                {connecting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Opening Intuit…</> : <><Link2 className="h-4 w-4 mr-2" /> Connect to QuickBooks</>}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* What syncs */}
        <Card className="sag-card">
          <CardHeader>
            <CardTitle className="text-lg font-heading">What will sync (Phase 2 rolling out next)</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="text-sm text-slate-700 space-y-2 list-disc list-inside">
              <li><strong>Customers</strong> — every SAG client auto-created in QBO (idempotent by email)</li>
              <li><strong>Invoices</strong> — every recurring or manual invoice auto-created in QBO with the same period + line items</li>
              <li><strong>Payments</strong> — every Stripe/Fiserv payment auto-posted against the matching QBO invoice</li>
            </ul>
            <p className="text-xs text-slate-500 mt-3">Setup once here → close the loop between your helpdesk and your books with zero manual reconciliation.</p>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
